import { Button, Typography } from '@mui/material';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { composeValidators, required } from 'helpers/validators';
import { IOption, IProduct } from 'interfaces';
import { useEffect, useState } from 'react';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import {
  productService,
  stockQuantityLocationService,
  stockQuantityService,
  transportDocumentActiveLineService
} from 'services/api';

interface TransportActiveDocumentLineFormProps {
  ddt;
  onSubmit: any;
  products: IOption[];
  setProduct: any;
}

const TransportActiveDocumentLineForm = (props: TransportActiveDocumentLineFormProps) => {
  const { ddt, onSubmit, products, setProduct } = props;

  const translationPrefix = 'pages.active-ddt.new-product';
  const { t } = useTranslation();
  const [maxUnits, setMaxUnits] = useState<number | null>(null);
  const [consumedUnits, setConsumedUnits] = useState<number>(0);
  const [selectedBatch, setSelectedBatch] = useState<IOption | null>(null);

  const [batchesOption, setBatchesOptions] = useState<IOption[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<IProduct>();

  // const [updateContext, setUpdateContext] = useState(false);

  useEffect(() => {
    setProduct(selectedProduct);
    if (selectedProduct) {
      const params = {
        product__id: selectedProduct.id ?? '',
        warehouse__id: ddt.warehouse_out ?? '',
        quantity__gt: 0
      };

      // ------------------------- costrutto per la gestione "COMBINATA" di StockQuantity & StockQuantityLocation -------------------------
      // Prima chiamata API
      stockQuantityService
        .getUnloadStocks(1000, 0, params)
        .then((res) => {
          const firstBatchData = res.results
            .filter((value) => value.free_quantity > 0)
            .map((value) => {
              let description = value.product_batch;
              if (value.expiration_date) description += `; Scad: ${value.expiration_date}`;
              description += `; Qty: ${value.free_quantity}`;
              return {
                id: crypto.randomUUID(),
                code: description,
                product_batch: value.product_batch,
                expiration_date: value.expiration_date,
                qty: value.free_quantity
              };
            });

          // Aggiornamento dello stato iniziale
          setBatchesOptions(firstBatchData);

          // Seconda chiamata API (una volta completata la prima)
          return stockQuantityLocationService.getUnloadStockLocation(1000, 0, params);
        })
        .then((res) => {
          const secondBatchData = res.results.map((value) => {
            let description = value.product_batch;
            if (value.expiration_date) description += `; Scad: ${value.expiration_date}`;
            description += `; Qty: ${value.quantity}; ${value.location__code}`;
            return {
              id: crypto.randomUUID(),
              code: description,
              product_batch: value.product_batch,
              expiration_date: value.expiration_date,
              location: value.location__code,
              qty: value.quantity
            };
          });

          // Aggiornamento dello stato combinando i dati di entrambe le chiamate
          setBatchesOptions((prevBatches) => [...prevBatches, ...secondBatchData]);
        });
    } else {
      setBatchesOptions([]);
    }
  }, [selectedProduct]);

  useEffect(() => {
    updateQty();
  }, [selectedBatch]);

  const updateQty = (value?: any) => {
    if (selectedBatch) {
      let qty = selectedBatch['qty'];

      ddt.lines.forEach((line) => {
        let productId;
        if (typeof line.product === 'object' && line.product !== null) {
          productId = line.product.id;
        } else {
          productId = line.product;
        }
        if (line.product_batch === selectedBatch['id'] && productId === selectedProduct.id) {
          qty -= line.quantity;
        }
      });

      if (value) {
        if (
          value.product.id === selectedProduct.id &&
          value.product_batch.id === selectedBatch['id']
        ) {
          qty -= value.quantity;
        }
      }

      const matchedBatch: any = batchesOption.find((batch) => batch.id === selectedBatch['id']);

      transportDocumentActiveLineService
        .getAlreadySelectedProducts({
          product: selectedProduct.id,
          product_batch: matchedBatch ? matchedBatch.product_batch : '',
          location: matchedBatch ? matchedBatch.location : ''
        })
        .then((res) => {
          setMaxUnits(qty);
          setConsumedUnits(res.selected_quantity);
        });
    }
  };

  const handleOnSubmit = (values: any) => {
    onSubmit({ ...values });
    setSelectedProduct(null);
    updateQty(values);
  };

  return (
    <div className="flex flex-wrap">
      <div className="w-full p-2">
        <ReactFinalForm
          onSubmit={handleOnSubmit}
          formId="addTransportActiveDocumentLine"
          hideControls={true}>
          <div className="flex flex-wrap mb-2 w-full">
            <div className="w-full flex gap-4">
              <div className="w-1/2">
                <IdentityField
                  name="product"
                  component={AutocompleteAdapter}
                  options={products}
                  label={t(`${translationPrefix}.product`)}
                  optionLabel="description"
                  type="text"
                  validate={composeValidators(required)}
                  setValue={setSelectedProduct}
                  getMethodService={productService}
                  required
                  submitObject
                />
              </div>
              <div className="w-1/2">
                <IdentityField
                  key={selectedProduct}
                  name="product_batch"
                  label={t(`${translationPrefix}.product_batch_expiration_date`)}
                  component={AutocompleteAdapter}
                  options={batchesOption}
                  type="text"
                  validate={required}
                  required
                  setValue={setSelectedBatch}
                  autoSelect={false}
                  submitObject
                />
              </div>
            </div>

            <div className="w-full flex gap-4 mt-4">
              <div className="w-full flex-col">
                {maxUnits !== null && (
                  <Typography color="text-primary-600">
                    <b>{`${t(`${translationPrefix}.aviable_units`, {
                      qty: maxUnits - consumedUnits
                      // alreadyUsedQty: consumedUnits
                    })}`}</b>
                  </Typography>
                )}
                <IdentityField
                  name="quantity"
                  component={TextFieldAdapter}
                  label={t(`${translationPrefix}.quantity`)}
                  type="number"
                />
              </div>
            </div>

            <div className="w-full mt-4 py-auto flex flex-wrap justify-end">
              <Button
                type="submit"
                form="addTransportActiveDocumentLine"
                variant="outlined"
                className="w-full">
                {t(`${translationPrefix}.add_line`)}
              </Button>
            </div>
          </div>
        </ReactFinalForm>
      </div>
    </div>
  );
};

export default TransportActiveDocumentLineForm;
